<script setup lang="ts">
import type { Store } from '@/views/contests/Components/types/global'

import MarketingFlowTriggerTypeBirthday from './MarketingFlowTriggerType/Birthday.vue'
import MarketingFlowTriggerTypeCheckin from './MarketingFlowTriggerType/Checkin.vue'
import MarketingFlowTriggerTypeIntervalOption from './MarketingFlowTriggerType/IntervalOption.vue'
import MarketingFlowTriggerTypeReward from './MarketingFlowTriggerType/Reward.vue'
import MarketingFlowTriggerTypeSegment from './MarketingFlowTriggerType/Segment.vue'
import MarketingFlowTriggerTypeStore from './MarketingFlowTriggerType/Store.vue'

import type { Icons } from '@/components/base/BaseIcon.vue'
import type { Audience, LoyaltyReward } from '@/types/types'
import type { TriggerSettings, TriggerType } from '../types/marketingFlow'

const { t } = useI18n()
const { isTriggerStepCompleted, isUsingTemplate } = useMarketingFlow()
const { isSupportConnection } = useLocalStorage()

defineProps<{
  stores: Store[]
  audiences: Audience[]
  isActive: boolean
  rewards?: LoyaltyReward[]
}>()

const triggerSettings = defineModel<TriggerSettings>()

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: TriggerSettings): void
  (e: 'goNextStep'): void
  (e: 'goPreviousStep'): void
  (e: 'onChanges', change: { isCritical: boolean; field?: string; value?: any }): void
  (e: 'fetchRewards'): void
  (e: 'updateStores')
}>()
const triggerType = ref<TriggerType>(null)
const selectedTriggerType = ref<TriggerType>(null)

const triggerTypes: { type: TriggerType; icon: Icons; label: string; isSupportConnectionOnly?: boolean }[] = [
  {
    type: 'SEGMENT_ENTRY' as TriggerType,
    icon: 'HeroArrowLeftOnRectangleIconOutline',
    label: 'pages.marketingFlow.marketingFlowEditPage.trigger.joinSegment'
  },
  {
    type: 'SEGMENT_EXIT' as TriggerType,
    icon: 'HeroArrowRightOnRectangleIconOutline',
    label: 'pages.marketingFlow.marketingFlowEditPage.trigger.leaveSegment'
  },
  {
    type: 'CHECKIN' as TriggerType,
    icon: 'IconoirShop',
    label: 'pages.marketingFlow.marketingFlowEditPage.trigger.checkin'
  },
  {
    type: 'SIGNUP' as TriggerType,
    icon: 'IconoirUserPlus',
    label: 'pages.marketingFlow.marketingFlowEditPage.trigger.signup',
    isSupportConnectionOnly: true
  },
  {
    type: 'BIRTHDAY' as TriggerType,
    icon: 'IconoirBirthdayCake',
    label: 'pages.marketingFlow.marketingFlowEditPage.trigger.birthday'
  },
  {
    type: 'REWARD_RETRIEVAL' as TriggerType,
    icon: 'IconoirUserStar',
    label: 'pages.marketingFlow.marketingFlowEditPage.trigger.rewardRetrieval'
  },
  {
    type: 'REWARD_NEAR_TO_UNLOCK' as TriggerType,
    icon: 'IconoirCompress',
    label: 'pages.marketingFlow.marketingFlowEditPage.trigger.rewardNearToUnlock'
  }
]

const selectType = (value: TriggerType) => {
  if (isUsingTemplate.value) {
    return
  }

  if (triggerType.value !== null) {
    triggerType.value = null
    return
  }

  triggerType.value = value
  selectedTriggerType.value = value

  triggerSettings.value.triggerType = value
  if (value === 'CHECKIN' || value === 'SIGNUP') {
    triggerSettings.value.selectedAudience = null
  }

  if (value === 'BIRTHDAY' && !triggerSettings.value.dateRelativeOffsetDays) {
    triggerSettings.value.dateRelativeOffsetDays = 0
  }

  if (value === 'BIRTHDAY') {
    triggerSettings.value.cooldownMinutes = null
    triggerSettings.value.maxTriggersPerUser = null
  }

  if (value === 'REWARD_RETRIEVAL') {
    triggerSettings.value.selectedRewards = [null]
  }

  if (value === 'REWARD_NEAR_TO_UNLOCK') {
    triggerSettings.value.pointTreshold = 10
    triggerSettings.value.selectedRewards = [null]
  } else {
    triggerSettings.value.pointTreshold = null
  }

  emit('onChanges', { isCritical: true, field: 'triggerType', value })
}

const handleBackButton = () => {
  if (triggerType.value !== null) {
    triggerType.value = null
  } else {
    emit('goPreviousStep')
  }
}

onMounted(() => {
  triggerType.value = triggerSettings.value.triggerType
  selectedTriggerType.value = triggerSettings.value.triggerType

  // Initialize birthday offset value (only before)
  triggerSettings.value.dateRelativeOffsetDays = Number(triggerSettings.value.dateRelativeOffsetDays) || 0

  // For BIRTHDAY trigger, ensure multipleTimeScenario is always true since it should be re-runnable yearly
  if (triggerSettings.value.triggerType === 'BIRTHDAY') {
    triggerSettings.value.maxTriggersPerUser = null
    triggerSettings.value.cooldownMinutes = null
  }
})
</script>

<template>
  <div class="flex min-h-20 w-full flex-row items-center border-b bg-muted px-5 py-3">
    <div class="flex flex-1 flex-col justify-center">
      <span class="font-semibold text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.title') }}
      </span>
      <span class="text-sm uppercase text-muted-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.subTitle') }}
      </span>
    </div>

    <div class="flex flex-row items-center">
      <Button v-if="!isUsingTemplate" variant="outline" class="mx-3" @click="handleBackButton">
        <span class="text-foreground">{{ t('global.words.back') }}</span>
      </Button>
      <Button
        variant="default-primary"
        :disabled="!isTriggerStepCompleted(triggerSettings)"
        @click="emit('goNextStep')"
      >
        {{ t('global.words.continue') }}
      </Button>
    </div>
  </div>
  <div class="flex flex-col overflow-auto" style="height: calc(100% - 80px)">
    <div
      v-for="trigger in triggerTypes"
      v-show="
        (triggerType === null || triggerType === trigger.type) &&
        (!trigger.isSupportConnectionOnly || isSupportConnection)
      "
      :key="trigger.type"
      class="flex min-h-20 w-full flex-row items-center border-b px-5 py-3"
      :class="!isUsingTemplate ? 'cursor-pointer' : ''"
      @click="selectType(trigger.type)"
    >
      <div class="flex flex-1 flex-row items-center">
        <div
          class="flex h-10 w-10 items-center justify-center rounded-lg"
          :class="selectedTriggerType === trigger.type ? 'bg-primary/10' : 'bg-muted'"
        >
          <BaseIcon
            :name="trigger.icon"
            class="h-6 w-6"
            :class="selectedTriggerType === trigger.type ? 'text-primary' : 'text-foreground-secondary'"
          />
        </div>
        <span class="ml-6 text-sm font-medium text-foreground mr-2">
          {{ t(trigger.label) }}
        </span>

        <BaseSupportConnection v-if="trigger.isSupportConnectionOnly" />
      </div>
      <BaseIcon
        v-if="!isUsingTemplate"
        name="HeroChevronRightIconSolid"
        class="h-5 w-5 text-foreground transition-transform"
        :class="triggerType === trigger.type ? 'rotate-90' : 'rotate-0'"
      />
    </div>

    <div v-if="triggerType !== null" class="flex w-full flex-col pt-4">
      <div
        v-if="triggerType === 'SEGMENT_ENTRY' || triggerType === 'SEGMENT_EXIT'"
        class="mb-0 flex w-full flex-row pr-5"
      >
        <div class="flex h-full flex-col items-center px-4">
          <div class="mt-[14px] flex min-h-7 w-7 items-center justify-center rounded-full border">
            <BaseIcon name="HeroSquare3Stack3DIconOutline" class="h-4 w-4 text-foreground" />
          </div>
        </div>

        <div class="flex flex-1 flex-col" style="width: calc(100% - 60px)">
          <div class="mb-4 flex flex-col rounded-lg border bg-muted p-4">
            <div class="flex flex-col justify-stretch">
              {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.segments') }}

              <label class="mb-1.5 mt-4 text-sm text-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.whichSegment') }}
                <span class="text-red-500">*</span>
              </label>
              <DropdownAudience
                v-model="modelValue.selectedAudience"
                :disabled="isActive"
                :audiences="audiences"
                :placeholder="t('global.multiselect.audiences.placeholder')"
                :filter-placeholder="t('global.multiselect.audiences.filterPlaceholder')"
                @update:model-value="
                  $emit('onChanges', { isCritical: false, field: 'selectedAudience', value: $event })
                "
              />
            </div>
          </div>
        </div>
      </div>

      <MarketingFlowTriggerTypeBirthday
        v-if="triggerType === 'BIRTHDAY'"
        v-model:trigger-settings="triggerSettings"
        @on-changes="emit('onChanges', $event)"
      />

      <MarketingFlowTriggerTypeCheckin
        v-if="triggerType === 'CHECKIN'"
        v-model:trigger-settings="triggerSettings"
        :audiences="audiences"
        :stores="stores"
        @on-changes="emit('onChanges', $event)"
      />

      <MarketingFlowTriggerTypeReward
        v-if="triggerType === 'REWARD_RETRIEVAL' || triggerType === 'REWARD_NEAR_TO_UNLOCK'"
        v-model:trigger-settings="triggerSettings"
        :rewards="rewards"
        @on-changes="emit('onChanges', $event)"
      />

      <MarketingFlowTriggerTypeStore
        v-model:trigger-settings="triggerSettings"
        :audiences="audiences"
        @on-changes="emit('onChanges', $event)"
        @update-stores="emit('updateStores')"
      />

      <MarketingFlowTriggerTypeSegment
        v-model:trigger-settings="triggerSettings"
        :audiences="audiences"
        :trigger-segment-id="triggerSettings.selectedAudience"
        @on-changes="emit('onChanges', $event)"
      />

      <MarketingFlowTriggerTypeIntervalOption
        v-if="triggerType !== 'BIRTHDAY'"
        v-model:trigger-settings="triggerSettings"
        @on-changes="emit('onChanges', $event)"
      />
    </div>
  </div>
</template>
